import { CustomsService } from '../../../services/customs-service';
import { DimensionUnit } from '../../../enums/dimension-unit';
import { InsuranceProviders } from '../../../enums/insurance-providers';

import { DEFAULT_OPTIONS } from '../csw.request-builders';

const getShipmentEmailNotificationTemplateId = (storeTemplateId, shipmentEmailNotificationTemplateId) => {
  if (shipmentEmailNotificationTemplateId !== undefined) {
    return shipmentEmailNotificationTemplateId;
  }
  return storeTemplateId || null;
};

const getDeliveryEmailNotificationTemplateId = (storeTemplateId, deliveryEmailNotificationTemplateId) => {
  if (deliveryEmailNotificationTemplateId !== undefined) {
    return deliveryEmailNotificationTemplateId;
  }
  return storeTemplateId || null;
};

const getPackingSlipTemplateId = (storeTemplateId, packingSlipTemplateId) => {
  if (packingSlipTemplateId !== undefined) {
    return packingSlipTemplateId;
  }
  return storeTemplateId || null;
};

const sanitizeOptions = (options) => {
  const cleanedOptions = Object.keys(options || {}).reduce((acc, cur) => {
    if (options[cur] === null || options[cur] === false) {
      return acc;
    }
    return {
      ...acc,
      [cur]: options[cur],
    };
  }, {});

  return cleanedOptions;
};

const sanitizeCustoms = (customs, reduxData) => {
  const { defaultCustomsData, defaultCurrency, defaultWeightUnit } = reduxData;

  const safeCustoms = CustomsService.fillCustomsWithDefaults(customs, defaultCustomsData, defaultCurrency, defaultWeightUnit);
  return safeCustoms;
};

const sanitizePackageTypeId = (packageTypeId) => {
  if (!packageTypeId || packageTypeId === 'undefined') {
    return null;
  }
  return packageTypeId;
};

export const sanitizePackages = (packages, sellerDimensionUnit, shouldIncludeInsuranceValue) => {
  return packages
    .filter((pkg) => !!pkg)
    .map((p) => {
      const dimensionUnit =
        (p?.dimensions?.unit || DimensionUnit.Unknown) === DimensionUnit.Unknown ? sellerDimensionUnit : p?.dimensions?.unit;
      const dimensions = p?.dimensions || {};
      return {
        ...p,
        packageId: undefined,
        packageTypeId: p.packageId ? sanitizePackageTypeId(p.packageId.split('-')[0]) : null,
        dimensions: {
          ...dimensions,
          length: dimensions.length || 0,
          width: dimensions.width || 0,
          height: dimensions.height || 0,
          unit: dimensionUnit,
        },
        insuredValue: {
          ...p.insuredValue,
          value: shouldIncludeInsuranceValue ? p?.insuredValue?.value || 0 : 0,
        },
      };
    });
};

export const getConfigurationNoCustoms = (shippingConfig, defaultDimensionUnit) => {
  const existingOptionIncludeReturnLabel = shippingConfig?.options?.includeReturnLabel;
  const includeReturnLabel = existingOptionIncludeReturnLabel || DEFAULT_OPTIONS.includeReturnLabel;

  const shouldIncludeInsuranceValue = shippingConfig.insuranceProvider && shippingConfig.insuranceProvider !== InsuranceProviders.None.name;

  const {
    availableSellerShippingServices,
    availableShippingOptions,
    displayScheme,
    fulfillmentPlanId,
    insuranceProvider,
    options,
    packages,
    sellerLabelProviderId,
    serviceCollectionGuid,
    serviceId,
    shipFromId,
    shipTo,
  } = shippingConfig;
  return {
    availableSellerShippingServices,
    availableShippingOptions,
    fulfillmentPlanId,
    insuranceProvider: shouldIncludeInsuranceValue ? insuranceProvider : InsuranceProviders.None.name,
    options: {
      ...DEFAULT_OPTIONS,
      ...sanitizeOptions(options),
      includeReturnLabel,
    },
    packages: sanitizePackages(packages, defaultDimensionUnit, shouldIncludeInsuranceValue),
    pickup: undefined,
    sellerLabelProviderId,
    serviceCollectionGuid,
    serviceId,
    shipFromId,
    shipTo,
    ...(displayScheme !== undefined ? { displayScheme } : {}),
  };
};

export const getRateConfiguration = (shippingConfig, reduxData) => {
  const baseConfiguration = getConfigurationNoCustoms(shippingConfig, reduxData.defaultDimensionUnit);
  if (baseConfiguration?.options) {
    // this fixes an issue with confirmation applying correct
    // and was copied from #3690
    baseConfiguration.options.confirmation =
      baseConfiguration.options?.confirmation?.confirmationId || baseConfiguration.options?.confirmationEnumId;
  }
  return {
    ...baseConfiguration,
    customs: sanitizeCustoms(shippingConfig.customs, reduxData),
    driverTip: null,
    email: shippingConfig.email,
    isReturn: shippingConfig.isReturn,
    shipDate: shippingConfig.shipDate,
  };
};

export const getSaveAndRateConfiguration = (fulfillmentPlan, reduxData) => {
  const baseConfiguration = getConfigurationNoCustoms(fulfillmentPlan, reduxData.defaultDimensionUnit);
  return {
    ...baseConfiguration,
    customs: fulfillmentPlan.properties?.needsCustoms ? sanitizeCustoms(fulfillmentPlan.customs, reduxData) : null,
    deliveryEmailNotificationTemplateId: getDeliveryEmailNotificationTemplateId(
      reduxData.storeDeliveryEmailNotificationTemplateId,
      fulfillmentPlan.deliveryEmailNotificationTemplateId,
    ),
    driverTip: null,
    shipmentEmailNotificationTemplateId: getShipmentEmailNotificationTemplateId(
      reduxData.storeShipmentEmailNotificationTemplateId,
      fulfillmentPlan.shipmentEmailNotificationTemplateId,
    ),
    packingSlipTemplateId: getPackingSlipTemplateId(reduxData.packingSlipTemplateId, fulfillmentPlan.packingSlipTemplateId),
    shipDate: fulfillmentPlan.shipDate,
  };
};
